import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/app/PageBits";
import { fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Download, UserCog } from "lucide-react";
import { toast } from "sonner";

export default function AuditLog() {
  const [list, setList] = useState([]);
  const [impOnly, setImpOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (flag) => {
    setLoading(true);
    try {
      const r = await api.get("/audit-log", { params: { impersonation_only: flag ? "true" : undefined } });
      setList(r.data || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(impOnly); /* eslint-disable-next-line */ }, [impOnly]);

  const exportCsv = async () => {
    try {
      const r = await api.get("/audit-log/export.csv", {
        responseType: "blob",
        params: { impersonation_only: impOnly ? "true" : undefined },
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = impOnly ? `audit_log_impersonation_${stamp}.csv` : `audit_log_${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Audit log"
        subtitle="Every mutation is stamped with actor, action, entity, and payload. Retained for compliance."
        actions={
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2" data-testid="audit-impersonation-toggle-wrap">
              <Switch
                id="imp-only"
                checked={impOnly}
                onCheckedChange={setImpOnly}
                data-testid="audit-impersonation-toggle"
              />
              <Label htmlFor="imp-only" className="text-xs cursor-pointer select-none">
                Only actions performed while impersonating
              </Label>
            </div>
            <Button data-testid="audit-export-btn" variant="outline" onClick={exportCsv}>
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          </div>
        }
      />
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Timestamp</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Actor</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Action</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Entity</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Details</th>
          </tr></thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.log_id} data-testid={`log-row-${l.log_id}`} className="border-t border-border align-top">
                <td className="px-4 py-3 tabular text-xs text-muted-foreground whitespace-nowrap">{fmtDate(l.timestamp)}</td>
                <td className="px-4 py-3 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{l.actor_email}</span>
                    {l.impersonating && (
                      <span
                        data-testid={`log-impersonating-badge-${l.log_id}`}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900 text-[10px] font-medium uppercase tracking-wider"
                        title={l.impersonated_by_email ? `Impersonated by ${l.impersonated_by_email}` : "Impersonated session"}
                      >
                        <UserCog className="h-3 w-3" /> Impersonated
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground">{l.actor_role}</div>
                </td>
                <td className="px-4 py-3 text-xs font-medium">{l.action}</td>
                <td className="px-4 py-3 text-xs">{l.entity} · <span className="text-muted-foreground tabular">{l.entity_id?.slice(0, 10)}</span></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {l.meta && <div>reason: {l.meta.reason || JSON.stringify(l.meta)}</div>}
                  {l.after && <div className="truncate max-w-md">after: {JSON.stringify(l.after)}</div>}
                </td>
              </tr>
            ))}
            {list.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground" data-testid="audit-empty-state">
                {impOnly ? "No impersonated-session actions recorded yet." : "No activity yet."}
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
